export const LEGACY_SLIDE_LIMITS = Object.freeze({
  rules: [
    {
      id: '5-quiz-legacy', label: 'Quiz', match: { type: ['pickAnswer'] }, limit: 5,
    },
    {
      id: '3-unscored-legacy',
      label: 'Unscored',
      match: { type: ['multipleChoice', 'imageChoice', 'openEnded', 'wordCloud', 'ideas', 'scale', 'pinOnImage', 'ideaBoard', 'ranking'] },
      limit: 3,
    },
  ],
  default: false,
})

export const UNLIMITED_SLIDE_LIMITS = Object.freeze({
  rules: [],
  default: false,
})

const MATCH_FIELDS = ['type', 'slideType']

const _isValidLimit = (value) => value === false || (Number.isInteger(value) && value >= 0)

const _isValidMatch = (match) => {
  if (!match || typeof match !== 'object' || Array.isArray(match)) return false
  const keys = Object.keys(match)
  if (keys.length === 0) return false
  return keys.every((key) => MATCH_FIELDS.includes(key)
    && Array.isArray(match[key])
    && match[key].length > 0
    && match[key].every((entry) => typeof entry === 'string'))
}

export const isValidDistinctTypeLimit = (value) => {
  if (value === undefined) return true
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  if (typeof value.id !== 'string' || value.id.length === 0) return false
  if (!Number.isInteger(value.limit) || value.limit < 0) return false
  if (value.label !== undefined && typeof value.label !== 'string') return false
  if (value.exclude === undefined) return true
  return _isValidMatch(value.exclude)
}

/**
 * Returns true if value is a well-formed slideLimits object.
 * @param {*} value
 * @returns {boolean}
 */
export const isValidSlideLimits = (value) => {
  if (!value || typeof value !== 'object') return false
  if (!Array.isArray(value.rules)) return false
  if (!_isValidLimit(value.default)) return false
  if (!isValidDistinctTypeLimit(value.distinctTypeLimit)) return false
  return value.rules.every((rule) => rule
    && typeof rule.id === 'string'
    && rule.id.length > 0
    && _isValidMatch(rule.match)
    && _isValidLimit(rule.limit)
    && (rule.label === undefined || typeof rule.label === 'string'))
}

/**
 * Returns maybe if valid, otherwise falls back to LEGACY_SLIDE_LIMITS.
 * @param {*} maybe
 * @returns {Object}
 */
export const resolveSlideLimits = (maybe) => (isValidSlideLimits(maybe) ? maybe : LEGACY_SLIDE_LIMITS)

/**
 * Compares a stored ruleset's presentation id against the currently open
 * presentation's id, tolerating string/number mismatches (route params are
 * strings, presentation.id may be a number).
 * @param {string|number|null|undefined} storedPresentationId
 * @param {string|number|null|undefined} openPresentationId
 * @returns {boolean}
 */
export const isSlideLimitsForOpenPresentation = (storedPresentationId, openPresentationId) => {
  if (storedPresentationId === null || storedPresentationId === undefined) return false
  if (openPresentationId === null || openPresentationId === undefined) return false
  return String(storedPresentationId) === String(openPresentationId)
}

/**
 * Picks the ruleset a slide-limit verdict should be evaluated against: the
 * per-presentation ruleset the server served for the open deck when it is valid
 * and belongs to that deck, otherwise the account-wide fallback.
 *
 * Shared by every consumer on purpose — the Present button and the slide-type
 * picker gate two halves of the same paywall, so they must never drift apart.
 *
 * When ignoreAllLimits is set (the monthly-participant treatment arm), the
 * per-deck and account rulesets are bypassed entirely and an unlimited ruleset
 * is returned, so slide-count / slide-type gating never fires regardless of what
 * restrictive ruleset the server carried over.
 *
 * @param {Object} params
 * @param {*} params.storedSlideLimits ruleset held in state for one presentation
 * @param {string|number|null|undefined} params.storedPresentationId presentation the stored ruleset was fetched for
 * @param {string|number|null|undefined} params.openPresentationId presentation currently open in the editor
 * @param {*} params.fallbackSlideLimits account-wide ruleset (planBenefit.slideLimits)
 * @param {boolean} [params.ignoreAllLimits] force the unlimited ruleset for the participant-cap arm
 * @param {Object|null} [params.distinctTypeLimitOverride] validated limit replacing the ruleset one
 * @returns {*}
 */
export const resolveServedSlideLimits = ({
  storedSlideLimits, storedPresentationId, openPresentationId, fallbackSlideLimits,
  ignoreAllLimits = false, distinctTypeLimitOverride = null,
}) => {
  if (ignoreAllLimits) return UNLIMITED_SLIDE_LIMITS
  const resolved = isValidSlideLimits(storedSlideLimits)
    && isSlideLimitsForOpenPresentation(storedPresentationId, openPresentationId)
    ? storedSlideLimits
    : fallbackSlideLimits
  if (!distinctTypeLimitOverride || !isValidDistinctTypeLimit(distinctTypeLimitOverride)) return resolved
  return { ...resolveSlideLimits(resolved), distinctTypeLimit: distinctTypeLimitOverride }
}
